import React, { useMemo, useState } from "react"
import {
  Button,
  Divider,
  Group,
  Icon,
  RangeSlider,
  SelectInput,
  Space,
  Title,
  TreeSelectInput,
  UnitRangeSlider,
  PopOver,
} from "components/common"
import { t } from "i18next"
import { useGetCitiesQuery, useGetProjectsQuery, useLazyGetLocalityQuery } from "features/shared/api"
import { skipToken } from "@reduxjs/toolkit/query"
import { configs } from "configs"
import { useGetUnitTypesWithSubTypesQuery } from "features/projects/api"
import { useSelector } from "react-redux"
import theme from "utils/themeVars"

const ModifyProjectPopover = ({ filterObject, setFilterObject }) => {
  const getCountryId = configs?.countryId
  const userInfo = useSelector(state => state?.app?.user?.info)
  const userPlatformKey = userInfo?.AgencyConfiguration?.Platform?.platformKey
  const { data: projects, isFetching: isFetchingProjects } = useGetProjectsQuery()
  const { data: cities, isFetching: isFetchingCities } = useGetCitiesQuery(getCountryId ?? skipToken)
  const [getLocations, { data: locations, isFetching: isFetchingLocations }] = useLazyGetLocalityQuery()
  const { data: unitTypes, isFetching: unitTypesLoading } = useGetUnitTypesWithSubTypesQuery()

  const [filters, setFilters] = useState(filterObject && { ...filterObject })
  const [open, setOpen] = useState(false)

  const handleOpenChange = visible => {
    setOpen(visible)
  }

  const areaRangesList = useMemo(
    () => [
      { key: "SqFt", value: [0, 11250] },
      { key: "SqYd", value: [0, 4000] },
      { key: "SqMt", value: [0, 51000] },
      { key: "Marla", value: [0, 50] },
      { key: "Kanal", value: [0, 100] },
    ],
    []
  )

  const unitTypesList = useMemo(() => {
    return [
      { key: "SqFt", label: t("Square Feet"), value: "sq_f" },
      { key: "SqYd", label: t("Square Yard"), value: "sq_yd" },
      { key: "SqMt", label: t("Square Meters"), value: "sq_m" },
      { key: "Marla", label: t("Marla"), value: "marla" },
      { key: "Kanal", label: t("Kanal"), value: "kanal" },
    ]
  }, [])

  const popoverContent = () => {
    return (
      <div style={{ width: "600px", padding: "12px" }}>
        <Group template="repeat(2, 1fr)" gap="20px 20px">
          <SelectInput
            label={t("Project")}
            placeholder={t("Please select project")}
            options={projects}
            value={filters?.["projectId"]}
            onChange={value => {
              setFilters({ ...filters, projectId: value })
            }}
            inputLoading={isFetchingProjects}
            style={{ width: "100%" }}
          />

          <TreeSelectInput
            value={filters?.unitTypeIds?.split(",")}
            label={t("Unit Types")}
            options={unitTypes || []}
            placeholder={t("Select Unit Types")}
            treeNodeFilterProp={"title"}
            handleOnChange={(it, isToggle) => {
              let obj = {}
              if (it.length > 0) {
                obj = { ...filters, unitTypeIds: it.join(",") }
              } else {
                obj = { ...filters }
                delete obj["unitTypeIds"]
              }
              setFilters(obj)
            }}
            style={{ width: "100%" }}
          />
          <SelectInput
            label={t("City")}
            placeholder={t("Select City")}
            options={cities || []}
            value={filters?.["cityIds"]}
            onChange={value => {
              getLocations(value)
              setFilters({ ...filters, cityIds: value })
            }}
            inputLoading={isFetchingCities}
            style={{ width: "100%" }}
          />
          <SelectInput
            label={t("Location")}
            placeholder={t("Select Location")}
            options={locations || []}
            value={filters?.["locationIds"]}
            onChange={value => {
              setFilters({ ...filters, locationIds: value })
            }}
            inputLoading={isFetchingLocations}
            style={{ width: "100%" }}
          />
        </Group>
        <div style={{ margin: "20px 0px" }}>
          <RangeSlider
            range={[0, 12000000]}
            value={
              filters?.["priceRange"]
                ? filters["priceRange"].split(",").map(Number)
                : filters?.["minPrice"] && filters?.["maxPrice"]
                  ? [Number(filters["minPrice"]), Number(filters["maxPrice"])]
                  : [0, 0]
            }
            setField={value => {
              const minPrice = Number.isFinite(Number(value[0])) ? Number(value[0]) : 0
              const maxPrice = Number.isFinite(Number(value[1])) ? Number(value[1]) : 0
              setFilters({ ...filters, priceRange: `${minPrice},${maxPrice}` })
            }}
            label={t("Price Range")}
          />
        </div>
        <UnitRangeSlider
          label={t("Area Range")}
          rangeList={areaRangesList}
          unitList={unitTypesList || []}
          defaultUnitKey={
            userPlatformKey === "bayut_ksa" ? { key: "SqMt", value: "sq_m" } : { key: "SqFt", value: "sq_f" }
          }
          value={
            filters?.["areaRange"]
              ? filters["areaRange"].split(",").map(Number)
              : filters?.["minArea"] && filters?.["maxArea"]
                ? [Number(filters["minArea"]), Number(filters["maxArea"])]
                : [0, 0]
          }
          setField={field => {
            const min = Number.isFinite(Number(field?.range?.[0])) ? Number(field?.range?.[0]) : 0
            const max = Number.isFinite(Number(field?.range?.[1])) ? Number(field?.range?.[1]) : 0
            setFilters({ ...filters, areaRange: `${min},${max}`, areaUnitKey: field?.rangeUnit })
          }}
        />
        <Divider className={"mbe-10"} />
        <div className="text-right">
          <Space size={12}>
            <Button
              size="small"
              loading={isFetchingProjects || isFetchingCities || isFetchingLocations || unitTypesLoading}
              text={t("Reset Filters")}
              onClick={() => {
                setFilters({})
                setFilterObject({})
                setOpen(false)
              }}
              color={theme["gray700"]}
              bordered
            />
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setFilterObject(filters)
                setOpen(false)
              }}
              text={t("Save")}
              loading={isFetchingProjects || isFetchingCities || isFetchingLocations || unitTypesLoading}
            />
          </Space>
        </div>
      </div>
    )
  }

  return (
    <PopOver
      content={popoverContent()}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      overlayInnerStyle={{ borderRadius: 12 }}
      title={
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px" }}>
            <Title className="mbe-0" level={4}>
              {t("Project Filters")}
            </Title>
            <Icon className={"pointer"} size="20px" icon="IoMdClose" onClick={() => setOpen(false)} />
          </div>
          <Divider style={{ margin: 0 }} />
        </>
      }
    >
      <Button type="primary-light" text={t("Modify")} icon="SvgIcSetting" size="16px" />
    </PopOver>
  )
}

export default ModifyProjectPopover
